// src\pages\Auth\excelUtils.js

import * as XLSX from 'xlsx';

/**
 * Parse Excel file to weekly plan data structure
 * @param {File} file - Excel file
 * @returns {Object} Parsed weekly plan data
 */
export const parseExcelToWeekData = async (file) => {
  try {
    console.log("Parsing Excel file:", file.name);
    
    // Read the file as array buffer
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    console.log("Excel sheets:", workbook.SheetNames);
    
    const weeklyPlan = {
      days: {}
    };

    // Process each day sheet
    workbook.SheetNames.forEach(sheetName => {
      // Skip instructions sheet
      if (sheetName === 'Instructions') return;
      
      console.log(`Processing sheet: ${sheetName}`);
      
      try {
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON with headers
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
        console.log(`${sheetName} data length:`, jsonData.length);
        
        // Safely get day name
        const dayName = getDayNameFromSheet(sheetName);
        if (!dayName) {
          console.warn(`Could not determine day name from sheet: ${sheetName}, skipping`);
          return;
        }
        
        console.log(`Processing day: ${dayName}`);
        
        weeklyPlan.days[dayName] = {
          name: dayName,
          total_duration: 0,
          data: {},
          media: {}
        };

        let currentBlock = null;
        let currentBlockData = null;
        let blockExercises = [];
        
        // Process each row starting from row 1 (skip header row 0)
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          
          // Skip empty rows
          if (!row || row.length === 0 || row.every(cell => cell === null || cell === undefined || cell === '')) {
            continue;
          }
          
          // Check if this row contains a new block (has Day and Block info)
          const rowDay = String(row[0] || '').trim().toUpperCase();
          const rowBlock = String(row[1] || '').trim();
          
          // If we have both day and block, it's a new block
          if (rowDay === dayName && rowBlock && rowBlock.endsWith('_BLOCK')) {
            // Save previous block if exists
            if (currentBlock && currentBlockData) {
              // Calculate total seconds for the block
              const totalSeconds = calculateTotalSecondsForBlock(currentBlockData);
              
              weeklyPlan.days[dayName].data[currentBlock] = {
                ...currentBlockData,
                tasks: [...blockExercises],
                total_seconds: totalSeconds,
                duration_minutes: Math.floor(totalSeconds / 60),
                duration_seconds: totalSeconds % 60
              };
              
              blockExercises = [];
            }
            
            // Start new block
            currentBlock = rowBlock;
            
            // Parse duration from column D (index 3)
            let duration = "40:00";
            if (row[3] !== undefined && row[3] !== null && row[3] !== '') {
              duration = parseDurationFromExcelCell(row[3]);
              console.log(`Parsed duration for ${rowBlock}: ${row[3]} -> ${duration}`);
            }
            
            // Parse working time and rest time
            let workingSeconds = 0;
            let restSeconds = 0;
            
            // Check for separate WorkingTime(s) and RestTime(s) columns (columns K and L, indices 10 and 11)
            if (row[10] !== undefined && row[10] !== null && row[10] !== '') {
              workingSeconds = parseNumberFromCell(row[10]);
              console.log(`Working seconds from column K: ${row[10]} -> ${workingSeconds}`);
            }
            
            if (row[11] !== undefined && row[11] !== null && row[11] !== '') {
              restSeconds = parseNumberFromCell(row[11]);
              console.log(`Rest seconds from column L: ${row[11]} -> ${restSeconds}`);
            }
            
            // If no working time specified, calculate from duration
            if (workingSeconds === 0) {
              workingSeconds = parseDurationToSeconds(duration);
              console.log(`No working time specified, using duration: ${duration} -> ${workingSeconds}s`);
            }
            
            // Create time segments from working/rest times
            const timeSegments = [{
              workingTime: workingSeconds,
              restTime: restSeconds,
              duration: workingSeconds + restSeconds,
              label: formatSegmentLabel(workingSeconds, restSeconds)
            }];
            
            console.log(`Time segments for ${rowBlock}:`, timeSegments);
            
            // Parse block transition from column M (index 12)
            let blockTransition = 0;
            if (row[12] !== undefined && row[12] !== null && row[12] !== '') {
              blockTransition = parseInt(row[12]) || 0;
            }
            
            currentBlockData = {
              title: String(row[2] || rowBlock.replace('_', ' ')),
              blockName: "Exercise",
              duration: duration,
              timeSegments: timeSegments,
              block_transition: blockTransition,
              cooldown: ""
            };
          }
          
          // If we're in a block and have exercise data (column E, index 4)
          if (currentBlock && row[4] !== undefined && row[4] !== null && row[4] !== '') {
            const exerciseName = String(row[4]).trim();
            if (exerciseName && !exerciseName.toUpperCase().includes('COOLDOWN') && 
                !exerciseName.toUpperCase().includes('WARM-UP') &&
                !exerciseName.toUpperCase().includes('COOLDOWN/NOTES') &&
                !exerciseName.toUpperCase().includes('REST DAY') &&
                !exerciseName.toUpperCase().includes('ACTIVE RECOVERY')) {
              
              const exercise = {
                id: `ex-${dayName}-${currentBlock}-${i}-${Date.now()}`,
                name: exerciseName,
                sets: row[5] !== undefined ? String(row[5] || "") : "",
                reps: row[6] !== undefined ? String(row[6] || "") : "",
                tempo: row[7] !== undefined ? String(row[7] || "") : "",
                notes: row[8] !== undefined ? String(row[8] || "") : "",
                restTime: row[9] !== undefined ? String(row[9] || "") : "" // Column J (index 9)
              };
              
              blockExercises.push(exercise);
            }
          }
          
          // Check for cooldown notes
          if (currentBlockData && row[4] && 
              (String(row[4]).toUpperCase().includes('COOLDOWN') ||
               String(row[4]).toUpperCase().includes('COOLDOWN/NOTES'))) {
            currentBlockData.cooldown = String(row[8] || row[4] || "");
          }
        }
        
        // Save the last block
        if (currentBlock && currentBlockData) {
          const totalSeconds = calculateTotalSecondsForBlock(currentBlockData);
          
          weeklyPlan.days[dayName].data[currentBlock] = {
            ...currentBlockData,
            tasks: blockExercises,
            total_seconds: totalSeconds,
            duration_minutes: Math.floor(totalSeconds / 60),
            duration_seconds: totalSeconds % 60
          };
        }
        
        // Calculate total duration for the day
        let dayTotalSeconds = 0;
        Object.values(weeklyPlan.days[dayName].data).forEach(block => {
          if (block.total_seconds) {
            dayTotalSeconds += block.total_seconds;
          }
        });
        weeklyPlan.days[dayName].total_duration = dayTotalSeconds;
        
        console.log(`Processed ${dayName}:`, weeklyPlan.days[dayName]);
        
      } catch (sheetError) {
        console.error(`Error processing sheet ${sheetName}:`, sheetError);
      }
    });
    
    console.log("Final parsed data structure:", weeklyPlan);
    return weeklyPlan;
    
  } catch (error) {
    console.error("Error parsing Excel file:", error);
    throw new Error(`Failed to parse Excel file: ${error.message}`);
  }
};

/**
 * Helper function to parse duration from Excel cell (handles Excel time formatting)
 */
function parseDurationFromExcelCell(cellValue) {
  if (cellValue === undefined || cellValue === null) {
    return "40:00";
  }
  
  console.log(`parseDurationFromExcelCell input: ${cellValue} (type: ${typeof cellValue})`);
  
  // If it's already a string in MM:SS format (or with apostrophe)
  if (typeof cellValue === 'string') {
    const cleanValue = cellValue.replace(/^'/, '').trim();
    
    // Check if it's in MM:SS format
    if (cleanValue.includes(':')) {
      const parts = cleanValue.split(':');
      if (parts.length === 2) {
        const minutes = parseInt(parts[0]) || 0;
        const seconds = parseInt(parts[1]) || 0;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
      }
    }
    
    // Try to parse as number
    const numValue = parseFloat(cleanValue);
    if (!isNaN(numValue)) {
      // If it's less than 60, assume minutes
      if (numValue < 60) {
        return `${Math.round(numValue)}:00`;
      }
      // Otherwise convert seconds to MM:SS
      const minutes = Math.floor(numValue / 60);
      const seconds = Math.round(numValue % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  }
  
  // If it's a number (Excel might store as number for time)
  if (typeof cellValue === 'number') {
    // Check if it's an Excel serial time (fraction of a day)
    // Excel stores times as fractions: 0.5 = 12:00:00, 0.25 = 6:00:00, etc.
    if (cellValue < 1) {
      // It's an Excel time serial
      const totalSeconds = Math.round(cellValue * 24 * 60 * 60); // Convert days to seconds
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      console.log(`Excel time serial ${cellValue} -> ${totalSeconds}s -> ${minutes}:${seconds.toString().padStart(2, '0')}`);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    
    // If it's a whole number, assume minutes
    if (cellValue < 60) {
      return `${Math.round(cellValue)}:00`;
    }
    
    // If it's > 60, assume seconds
    const minutes = Math.floor(cellValue / 60);
    const seconds = Math.round(cellValue % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  
  // Default fallback
  return "40:00";
}

/**
 * Helper function to parse number from cell (handles text with 's' suffix)
 */
function parseNumberFromCell(cellValue) {
  if (cellValue === undefined || cellValue === null || cellValue === '') {
    return 0;
  }
  
  // Handle Excel time serials (fraction of a day)
  if (typeof cellValue === 'number' && cellValue < 1) {
    // Convert Excel time to seconds
    const seconds = Math.round(cellValue * 24 * 60 * 60);
    console.log(`Excel time serial ${cellValue} -> ${seconds} seconds`);
    return seconds;
  }
  
  // Remove any non-numeric characters except minus sign and decimal point
  const cleanValue = String(cellValue)
    .replace(/^'/, '') // Remove leading apostrophe
    .replace(/s$/i, '') // Remove 's' suffix
    .replace(/[^\d.-]/g, ''); // Remove all non-numeric except . and -
  
  const numValue = parseFloat(cleanValue);
  return isNaN(numValue) ? 0 : Math.round(numValue);
}

/**
 * Helper function to calculate total seconds for a block
 */
function calculateTotalSecondsForBlock(blockData) {
  if (!blockData) return 2400;
  
  // If we have time segments, sum them up
  if (blockData.timeSegments && blockData.timeSegments.length > 0) {
    const total = blockData.timeSegments.reduce((sum, segment) => {
      return sum + (segment.duration || 0);
    }, 0);
    console.log(`Calculated from timeSegments: ${total} seconds`);
    return total;
  }
  
  // Otherwise parse from duration string
  const total = parseDurationToSeconds(blockData.duration);
  console.log(`Calculated from duration ${blockData.duration}: ${total} seconds`);
  return total;
}

/**
 * Helper function to parse duration string to seconds
 */
function parseDurationToSeconds(durationStr) {
  if (!durationStr) {
    console.log(`No duration string, returning 2400`);
    return 2400;
  }
  
  console.log(`parseDurationToSeconds input: ${durationStr}`);
  
  if (typeof durationStr === 'string' && durationStr.includes(':')) {
    try {
      const parts = durationStr.split(':');
      const minutes = parseInt(parts[0]) || 0;
      const seconds = parseInt(parts[1]) || 0;
      const total = minutes * 60 + seconds;
      console.log(`Parsed MM:SS: ${minutes}m ${seconds}s = ${total}s`);
      return total;
    } catch (error) {
      console.error(`Error parsing duration string ${durationStr}:`, error);
      return 2400;
    }
  }
  
  // Try to parse as number
  const numValue = parseFloat(durationStr);
  if (!isNaN(numValue)) {
    // If it's less than 60, assume minutes
    if (numValue < 60) {
      const total = Math.round(numValue * 60);
      console.log(`Assuming minutes: ${numValue}min = ${total}s`);
      return total;
    }
    // Otherwise assume seconds
    console.log(`Assuming seconds: ${numValue}s`);
    return Math.round(numValue);
  }
  
  console.log(`Could not parse ${durationStr}, returning 2400`);
  return 2400; // Default 40 minutes
}

/**
 * Helper function to format segment label
 */
function formatSegmentLabel(workingTime, restTime) {
  const workingMinutes = Math.floor(workingTime / 60);
  const workingSeconds = workingTime % 60;
  
  let label = `${workingMinutes}:${workingSeconds.toString().padStart(2, '0')}`;
  
  if (restTime > 0) {
    const restMinutes = Math.floor(restTime / 60);
    const restSeconds = restTime % 60;
    label += ` + ${restMinutes}:${restSeconds.toString().padStart(2, '0')}R`;
  }
  
  return label;
}

/**
 * Helper function to get day name from sheet name
 */
function getDayNameFromSheet(sheetName) {
  const sheetUpper = sheetName.toUpperCase();
  
  // Map common abbreviations
  const dayMap = {
    'MON': 'MONDAY',
    'TUE': 'TUESDAY',
    'WED': 'WEDNESDAY',
    'THU': 'THURSDAY',
    'THUR': 'THURSDAY',
    'FRI': 'FRIDAY',
    'SAT': 'SATURDAY',
    'SUN': 'SUNDAY',
    'MONDAY': 'MONDAY',
    'TUESDAY': 'TUESDAY',
    'WEDNESDAY': 'WEDNESDAY',
    'THURSDAY': 'THURSDAY',
    'FRIDAY': 'FRIDAY',
    'SATURDAY': 'SATURDAY',
    'SUNDAY': 'SUNDAY'
  };
  
  // Check exact matches first
  if (dayMap[sheetUpper]) {
    return dayMap[sheetUpper];
  }
  
  // Check if sheet name contains day name
  const days = ['MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY', 'SUNDAY'];
  for (const day of days) {
    if (sheetUpper.includes(day.substring(0, 3))) {
      return day;
    }
  }
  
  // Return as-is if no match
  return sheetUpper;
}

/**
 * Download weekly plan as Excel file with separate WorkingTime and RestTime columns
 */
/**
 * Download weekly plan as Excel file with separate WorkingTime and RestTime columns
 */
export const downloadWeekToExcel = (weekData) => {
  try {
    if (!weekData || !weekData.full_week) {
      console.error("No data available for download");
      return false;
    }

    // Create a new workbook
    const workbook = XLSX.utils.book_new();
    
    // Add instructions sheet
    const instructionsData = [
      ["4 HUNNID Workout Plan - Weekly Template"],
      ["Created: " + new Date().toLocaleDateString()],
      [""],
      ["Instructions:"],
      ["1. Each row represents one exercise"],
      ["2. Add new blocks by adding new rows with Block name (A_BLOCK, B_BLOCK, etc.)"],
      ["3. WorkingTime: Total working time in SECONDS (e.g., 900 for 15 minutes)"],
      ["4. RestTime: Total rest time in SECONDS (e.g., 60 for 1 minute)"],
      ["5. BlockTransition is transition time between blocks in seconds"],
      ["6. Duration: Use MM:SS format (e.g., 25:00 for 25 minutes, 1:30 for 1 minute 30 seconds)"],
      [""],
      ["COLUMN GUIDE:"],
      ["Day: MONDAY, TUESDAY, etc."],
      ["Block: A_BLOCK, B_BLOCK, C_BLOCK, etc."],
      ["Title: Block title/description"],
      ["Duration: Total block duration in MM:SS format (e.g., 25:00, 1:30)"],
      ["Exercise: Exercise name"],
      ["Sets: Number of sets"],
      ["Reps: Repetitions or time"],
      ["Tempo: Tempo notation"],
      ["Notes: Additional instructions"],
      ["ExerciseRestTime: Rest time between sets (seconds)"],
      ["WorkingTime(s): Working time in seconds"],
      ["RestTime(s): Rest time in seconds"],
      ["BlockTransition: Transition time to next block (seconds)"]
    ];
    
    const instructionsSheet = XLSX.utils.aoa_to_sheet(instructionsData);
    XLSX.utils.book_append_sheet(workbook, instructionsSheet, "Instructions");

    // Helper function to format duration for Excel
    const formatDurationForExcel = (duration) => {
      if (!duration && duration !== 0) return "40:00";
      
      // Convert to string first
      const durationStr = String(duration);
      
      // Check if it's already in MM:SS format
      if (typeof durationStr === 'string' && durationStr.includes && durationStr.includes(':')) {
        const parts = durationStr.split(':');
        if (parts.length >= 2) {
          const minutes = parseInt(parts[0]) || 0;
          const seconds = parseInt(parts[1]) || 0;
          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
        return durationStr;
      }
      
      // Try to parse as number
      const numValue = parseFloat(durationStr);
      if (!isNaN(numValue)) {
        if (numValue >= 60) {
          // If it's 60 or more, assume it's seconds
          const minutes = Math.floor(numValue / 60);
          const seconds = Math.round(numValue % 60);
          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        } else {
          // If it's less than 60, assume it's minutes
          const minutes = Math.floor(numValue);
          const seconds = Math.round((numValue - minutes) * 60);
          return `${minutes}:${seconds.toString().padStart(2, '0')}`;
        }
      }
      
      return "40:00";
    };

    // Add each day as a separate sheet
    Object.entries(weekData.full_week).forEach(([dayName, dayData]) => {
      const rows = [];
      
      // Header row
      rows.push([
        "Day", "Block", "Title", "Duration", "Exercise", 
        "Sets", "Reps", "Tempo", "Notes", "ExerciseRestTime", 
        "WorkingTime(s)", "RestTime(s)", "BlockTransition"
      ]);
      
      // Add each block
      Object.entries(dayData.data || {}).forEach(([blockKey, blockData]) => {
        if (!blockKey.endsWith("_BLOCK")) return;
        
        console.log(`Processing block ${blockKey}:`, blockData);
        
        // Get working and rest times
        let workingSeconds = 0;
        let restSeconds = 0;
        
        if (blockData.timeSegments && blockData.timeSegments.length > 0) {
          const segment = blockData.timeSegments[0];
          workingSeconds = segment.workingTime || 0;
          restSeconds = segment.restTime || 0;
          console.log(`Time segments found: working=${workingSeconds}, rest=${restSeconds}`);
        } else {
          // Calculate from duration
          workingSeconds = blockData.total_seconds || parseDurationToSeconds(blockData.duration || "40:00");
          restSeconds = 0;
          console.log(`No time segments, using duration: ${workingSeconds} seconds`);
        }
        
        // Format duration for Excel - FIXED: Preserve MM:SS format
        const durationDisplay = formatDurationForExcel(blockData.duration);
        console.log(`Formatted duration: ${blockData.duration} -> ${durationDisplay}`);
        
        // Add block header row
        rows.push([
          dayName,
          blockKey,
          blockData.title || blockKey.replace("_", " "),
          durationDisplay, // Now in MM:SS format like "25:00" or "1:30"
          "",
          "",
          "",
          "",
          "",
          "",
          workingSeconds,
          restSeconds,
          blockData.block_transition || 0
        ]);
        
        // Add exercises
        const tasks = blockData.tasks || [];
        tasks.forEach((task, index) => {
          rows.push([
            "", // Empty day cell
            "", // Empty block cell
            "", // Empty title cell
            index === 0 ? durationDisplay : "", // Duration only on first exercise
            task.name || "",
            task.sets || "",
            task.reps || "",
            task.tempo || "",
            task.notes || "",
            task.restTime || "",
            "", "", "" // Empty time columns for exercise rows
          ]);
        });
        
        // Add cooldown if exists
        if (blockData.cooldown) {
          rows.push([
            "", "", "", "",
            "Cooldown/Notes", "", "", "",
            blockData.cooldown, "", "", "", ""
          ]);
        }
      });
      
      const daySheet = XLSX.utils.aoa_to_sheet(rows);
      
      // Set column widths for better readability
      const colWidths = [
        { wch: 10 }, // Day
        { wch: 10 }, // Block
        { wch: 12 }, // Title
        { wch: 10 }, // Duration
        { wch: 25 }, // Exercise
        { wch: 6 },  // Sets
        { wch: 6 },  // Reps
        { wch: 8 },  // Tempo
        { wch: 20 }, // Notes
        { wch: 15 }, // ExerciseRestTime
        { wch: 15 }, // WorkingTime(s)
        { wch: 12 }, // RestTime(s)
        { wch: 15 }  // BlockTransition
      ];
      
      daySheet['!cols'] = colWidths;
      
      // Set number format for Duration column (column D, index 3) to display time properly
      // Find the range of the sheet
      const range = XLSX.utils.decode_range(daySheet['!ref']);
      
      // Apply custom number format for Duration column (column D = index 3)
      for (let row = range.s.r + 1; row <= range.e.r; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: 3 }); // Column D (index 3)
        const cell = daySheet[cellAddress];
        if (cell) {
          // Set custom format for time (mm:ss)
          cell.z = 'mm:ss';
        }
      }
      
      XLSX.utils.book_append_sheet(workbook, daySheet, dayName.substring(0, 3));
    });

    // Generate file name
    const timestamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
    const fileName = `Workout_Plan_${timestamp}.xlsx`;
    
    // Use the standard writeFile method
    XLSX.writeFile(workbook, fileName);
    
    console.log("Excel file downloaded successfully:", fileName);
    return true;
    
  } catch (error) {
    console.error("Error downloading Excel:", error);
    console.error("Error details:", error.message);
    console.error("Error stack:", error.stack);
    return false;
  }
};

/**
 * Helper function to format duration for display
 */
function formatDurationForDisplay(duration) {
  if (!duration) return "40:00";
  
  if (typeof duration === 'string' && duration.includes(':')) {
    // Already in MM:SS format, just ensure seconds are 2-digit
    const parts = duration.split(':');
    if (parts.length === 2) {
      const minutes = parseInt(parts[0]) || 0;
      const seconds = parseInt(parts[1]) || 0;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
  }
  
  // Try to parse as number
  const numValue = parseFloat(duration);
  if (!isNaN(numValue)) {
    // If it's a large number, assume seconds
    if (numValue >= 60) {
      const minutes = Math.floor(numValue / 60);
      const seconds = Math.round(numValue % 60);
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
    // Otherwise assume minutes
    const minutes = Math.floor(numValue);
    const seconds = Math.round((numValue - minutes) * 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  
  return "40:00";
}